import type { Duration, OperationNodeSpec, TransferOptions } from '@/graph/domain/types';
import type { Store } from '../types';
import type { RecommendedInstrumentRef } from '@/components/modals/AddStepModal/types';
import type { Checkpoint } from '@/graph/domain/types';
import type {
  TransferRowFormState,
  NewDestinationDraft,
} from './transferHelpers';
import { isLatestMaterial } from './transferHelpers';

type GetState = () => Store;

type TransferSharedConfig = {
  operationTypeId: string;
  operationDisplayName: string;
  dataExpected?: boolean;
  row: TransferRowFormState;
  transferOptions: TransferOptions;
  duration?: Duration;
  notes?: string;
  comments?: string;
  recommendedInstrument?: RecommendedInstrumentRef[];
  checkpoints?: Checkpoint[];
  operationTemplate?: {
    operationTypeId: string;
    parameters?: Array<{
      parameterName?: string;
      value?: unknown;
      unit?: string;
      fieldType?: string;
      required?: boolean;
      tags?: string[];
    }>;
    hideParamsMap?: Record<string, boolean>;
    translationTemplate?: string;
  };
  approvers?: any;
  notifications?: any;
  destinationMaterialId?: string;
  destinationDraft?: NewDestinationDraft;
};

export interface AddStepBatchInput {
  sourceMaterialIds: string[];
  operationSpec?: OperationNodeSpec;
  transferSharedConfig?: TransferSharedConfig;
}

export interface AddStepBatchResult {
  sourceMaterialIds: string[];
  operationIds: string[];
  downstreamMaterialIds: string[];
}

function uniqueOrdered(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function filterLatestMaterialIds(get: GetState, ids: string[]): string[] {
  const state = get();
  return ids.filter(
    (id) =>
      Boolean(state.graphData.materialNodes[id]) &&
      isLatestMaterial(id, state.graphData.flowEdges),
  );
}

export function createBatchStepActions(get: GetState) {
  return {
    addStepBatch(input: AddStepBatchInput): AddStepBatchResult {
      const dedupedIds = uniqueOrdered(input.sourceMaterialIds ?? []);
      const sourceMaterialIds = filterLatestMaterialIds(get, dedupedIds);

      if (sourceMaterialIds.length === 0) {
        throw new Error('No valid latest source materials selected for batch add step');
      }

      if (input.transferSharedConfig) {
        const overlapDestinationId =
          input.transferSharedConfig.destinationMaterialId ??
          input.transferSharedConfig.row.destinationMaterialId ??
          null;
        if (overlapDestinationId && sourceMaterialIds.includes(overlapDestinationId)) {
          throw new Error('Transfer destination cannot be one of the selected source materials');
        }

        let currentDestinationMaterialId: string | undefined =
          input.transferSharedConfig.destinationMaterialId;
        let currentDestinationDraft: NewDestinationDraft | undefined =
          input.transferSharedConfig.destinationDraft;

        const operationIds: string[] = [];
        const downstreamMaterialIds: string[] = [];

        for (const sourceMaterialId of sourceMaterialIds) {
          const rowForStep: TransferRowFormState = {
            ...input.transferSharedConfig.row,
            destinationMaterialId: currentDestinationMaterialId ?? null,
            destinationDraft: currentDestinationDraft ?? null,
          };

          const result = get().addTransferStep({
            sourceMaterialId,
            operationTypeId: input.transferSharedConfig.operationTypeId,
            operationDisplayName: input.transferSharedConfig.operationDisplayName,
            dataExpected: input.transferSharedConfig.dataExpected,
            row: rowForStep,
            transferOptions: input.transferSharedConfig.transferOptions,
            destinationMaterialId: currentDestinationMaterialId,
            destinationDraft: currentDestinationDraft,
            duration: input.transferSharedConfig.duration,
            notes: input.transferSharedConfig.notes,
            comments: input.transferSharedConfig.comments,
            recommendedInstrument: input.transferSharedConfig.recommendedInstrument,
            checkpoints: input.transferSharedConfig.checkpoints,
            operationTemplate: input.transferSharedConfig.operationTemplate,
            approvers: input.transferSharedConfig.approvers,
            notifications: input.transferSharedConfig.notifications,
          });

          operationIds.push(result.operationId);
          downstreamMaterialIds.push(result.destinationPostMaterialId);
          currentDestinationMaterialId = result.destinationPostMaterialId;
          currentDestinationDraft = undefined;
        }

        return {
          sourceMaterialIds,
          operationIds,
          downstreamMaterialIds,
        };
      }

      if (!input.operationSpec) {
        throw new Error('operationSpec is required for non-transfer batch add step');
      }

      const operationIds: string[] = [];
      const downstreamMaterialIds: string[] = [];

      for (const sourceMaterialId of sourceMaterialIds) {
        const result = get().addStep({
          sourceMaterialId,
          operationSpec: input.operationSpec,
        });
        operationIds.push(result.operationId);
        downstreamMaterialIds.push(result.downstreamMaterialId);
      }

      return {
        sourceMaterialIds,
        operationIds,
        downstreamMaterialIds,
      };
    },
  };
}
